import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { FlightFareBundles } from "@repo/ui/types/cms/flight";

import { useAppSelector } from "../hooks";

export interface BundleSelection {
  outbound?: string; // bundleCode
  inbound?: string; // bundleCode
}

export interface FareBundleState {
  availableBundles: FlightFareBundles["bundles"];
  isLoading: boolean;
  error?: string;
}

const initialState: FareBundleState = {
  availableBundles: [],
  isLoading: false,
  error: undefined,
};

const fareBundleSlice = createSlice({
  name: "bundle",
  initialState,
  reducers: {
    setAvailableBundles: (state, action: PayloadAction<FlightFareBundles["bundles"]>) => {
      state.availableBundles = action.payload;
      state.error = undefined;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    resetBundleState: (state) => {
      state.availableBundles = [];
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

// Selectors/Hooks
export const useFareBundleState = () => useAppSelector((state) => state.bundle);
export const useAvailableBundles = () => useAppSelector((state) => state.bundle.availableBundles);

// Helper selector to get bundle by code
export const useBundleByCode = (bundleCode?: string) =>
  useAppSelector((state) =>
    bundleCode ? state.bundle.availableBundles.find((b) => b.bundleCode === bundleCode) : undefined,
  );

export const { setAvailableBundles, setLoading, setError, resetBundleState } =
  fareBundleSlice.actions;

export default fareBundleSlice.reducer;
